// RoomService
//
// This service monitors all platform initialization and stage changes starting
// with the browser window's onLoad event up through the room equipment going
// online. It is the single source of truth for the application's state.
//
// It provides methods for listening for changes in online state and for changes
// in initialization state.
//
// It provides convenience methods for getting the controller and room objects.
//
// When RoomService reports that it is online, the following will be true.
//
//  1. The browser's window document is loaded.
//  2. The deviceReady event has fired.
//  3. We are authenticated to the database.
//  4. The equipmentTypes are $loaded.
//  5. The controller is $loaded.
//  6. The room is $loaded.
//  7. The room's controllers are loaded (via room.$getControllers) -- only if
//     in the browser environment.
//  8. The room's control events are $loaded.
//  9. The room's site is $loaded.
// 10. The room's company is $loaded.
// 11. The room is online (all equipment is connected).
//
// Finally, RoomService takes care of forcing state transitions back to 'start'
// when we are unauthenticated, the controller gets unpaired, or the room goes
// offline.

'use strict';

var defaults = require('../../defaults/Defaults');
var idbKeyval = require('idb-keyval');
require('./KayleeService');
var log = require('../../log/Log').channel('RoomService');
var moment = require('moment');
require('./SharedPrefs');
var StateMachine = require('./state-machine.min');
var sutl = require('../../sutl/Sutl');

angular.module('inara').factory('RoomService', RoomService);

RoomService.$inject = [
    '$q',
    '$timeout',
    '$state',
    '$injector',
    'SharedPrefs',
    'KayleeService',
    'LanguageService',
];

function inject($injector, name, envs) {
    return envs.includes(process.env.appEnv) ? $injector.get(name) : null;
}

function RoomService($q, $timeout, $state, $injector, SharedPrefs, KayleeService, LanguageService) {
    var DeviceService = inject($injector, 'DeviceService', ['cordova']);
    var $rootScope = inject($injector, '$rootScope', ['browser']);
    var connectionStateChangedListener = null;

    var fsm = StateMachine.create({
        events: [
            { name: 'goToStart', from: 'none', to: 'start' },
            { name: 'loadDocument', from: 'start', to: 'documentLoading' },
            { name: 'handleDocumentLoaded', from: 'documentLoading', to: 'documentLoaded' },
            { name: 'loadDevice', from: 'documentLoaded', to: 'deviceLoading' },
            { name: 'handleDeviceLoaded', from: 'deviceLoading', to: 'deviceLoaded' },
            {
                name: 'waitForConnection',
                from: [
                    'deviceLoaded',
                    'waitingForAuth',
                    'unauthenticated',
                    'authenticated',
                    'kayleeLoading',
                    'waitingForKayleeEnabled',
                    'waitingForKayleeVersion',
                    'kayleeLoaded',
                    'controllerLoading',
                    'controllerLoaded',
                    'controllerUnpaired',
                    'roomLoading',
                    'roomLoaded',
                    'roomOffline',
                    'roomOnline',
                ],
                to: 'waitingForConnection',
            },
            {
                name: 'waitForAuth',
                from: [
                    'waitingForConnection',
                    'authenticated',
                    'unauthenticated',
                    'kayleeLoading',
                    'waitingForKayleeEnabled',
                    'waitingForKayleeVersion',
                    'kayleeLoaded',
                    'controllerLoading',
                    'controllerLoaded',
                    'controllerUnpaired',
                    'roomLoading',
                    'roomLoaded',
                    'roomOffline',
                    'roomOnline',
                ],
                to: 'waitingForAuth',
            },
            {
                name: 'handleUnauth',
                from: [
                    'waitingForAuth',
                    'authenticated',
                    'unauthenticated',
                    'kayleeLoading',
                    'waitingForKayleeEnabled',
                    'waitingForKayleeVersion',
                    'kayleeLoaded',
                    'controllerLoading',
                    'controllerLoaded',
                    'controllerUnpaired',
                    'roomLoading',
                    'roomLoaded',
                    'roomOffline',
                    'roomOnline',
                ],
                to: 'unauthenticated',
            },
            {
                name: 'handleAuth',
                from: [
                    'waitingForAuth',
                    'authenticated',
                    'unauthenticated',
                    'kayleeLoading',
                    'waitingForKayleeEnabled',
                    'waitingForKayleeVersion',
                    'kayleeLoaded',
                    'controllerLoading',
                    'controllerLoaded',
                    'controllerUnpaired',
                    'roomLoading',
                    'roomLoaded',
                    'roomOffline',
                    'roomOnline',
                ],
                to: 'authenticated',
            },
            {
                name: 'loadKaylee',
                from: ['authenticated', 'waitingForConnection'],
                to: 'kayleeLoading',
            },
            {
                name: 'waitForKayleeEnabled',
                from: [
                    'kayleeLoading',
                    'waitingForKayleeEnabled',
                    'waitingForKayleeVersion',
                    'controllerLoading',
                    'controllerLoaded',
                    'controllerUnpaired',
                    'roomLoading',
                    'roomLoaded',
                    'roomOnline',
                    'roomOffline',
                ],
                to: 'waitingForKayleeEnabled',
            },
            {
                name: 'waitForKayleeVersion',
                from: [
                    'kayleeLoading',
                    'waitingForKayleeEnabled',
                    'waitingForKayleeVersion',
                    'controllerLoading',
                    'controllerLoaded',
                    'controllerUnpaired',
                    'roomLoading',
                    'roomLoaded',
                    'roomOnline',
                    'roomOffline',
                ],
                to: 'waitingForKayleeVersion',
            },
            {
                name: 'handleKayleeLoaded',
                from: ['kayleeLoading', 'waitingForKayleeEnabled', 'waitingForKayleeVersion'],
                to: 'kayleeLoaded',
            },
            { name: 'loadController', from: 'kayleeLoaded', to: 'controllerLoading' },
            { name: 'handleControllerLoaded', from: 'controllerLoading', to: 'controllerLoaded' },
            {
                name: 'handleControllerUnpaired',
                from: [
                    'controllerLoaded',
                    'roomLoading',
                    'roomLoaded',
                    'roomOffline',
                    'roomOnline',
                ],
                to: 'controllerUnpaired',
            },
            {
                name: 'loadRoom',
                from: ['controllerLoaded', 'controllerUnpaired'],
                to: 'roomLoading',
            },
            { name: 'handleRoomLoaded', from: 'roomLoading', to: 'roomLoaded' },
            { name: 'handleRoomOffline', from: ['roomLoaded', 'roomOnline'], to: 'roomOffline' },
            { name: 'handleRoomOnline', from: ['roomLoaded', 'roomOffline'], to: 'roomOnline' },
        ],
        callbacks: {
            ongoToStart: onGoToStart,
            onloadDocument: onLoadDocument,
            onhandleDocumentLoaded: onHandleDocumentLoaded,
            onloadDevice: onLoadDevice,
            onhandleDeviceLoaded: onHandleDeviceLoaded,
            onwaitForConnection: onWaitForConnection,
            onwaitForAuth: onWaitForAuth,
            onhandleUnauth: onHandleUnauth,
            onhandleAuth: onHandleAuth,
            onloadKaylee: onLoadKaylee,
            onwaitForKayleeEnabled: onWaitForKayleeEnabled,
            onwaitForKayleeVersion: onWaitForKayleeVersion,
            onhandleKayleeLoaded: onHandleKayleeLoaded,
            onloadController: onLoadController,
            onhandleControllerLoaded: onHandleControllerLoaded,
            onhandleControllerUnpaired: onHandleControllerUnpaired,
            onloadRoom: onLoadRoom,
            onhandleRoomLoaded: onHandleRoomLoaded,
            onbeforehandleRoomOffline: onBeforeHandleRoomOffline,
            onhandleRoomOffline: onHandleRoomOffline,
            onbeforehandleRoomOnline: onBeforeHandleRoomOnline,
            onhandleRoomOnline: onHandleRoomOnline,
        },
    });

    var knownKayleeVersion = 2;
    var kayleeInfo = null;
    var fbUser = null;
    var controller = null;
    var factoryInstallMode = null;
    var roomId = null;
    var room = null;
    var remoteControlSessions = null;
    var uiPreview = false; // Mira Connect preview UI
    var waitForConnectedTimeout = null;
    var authRetryTimeout = null;
    var authRetryDelayMs = 30000;
    var prevOnlineState = null;
    var beforeOnlineListeners = [];
    var onlineListeners = [];
    var stateListeners = [];
    var prevAuthUid = null;
    var refreshRemoteControlCodeTimeout = null;
    var remoteControlSessionExpirationTimeout = null;
    var shouldResetRemoteControlCode = true;

    if (process.env.appEnv === 'browser') {
        $rootScope.$on(
            '$stateChangeSuccess',
            function (event, toState, toParams, fromState, fromParams) {
                if (toState.name !== 'start') return;

                if (KayleeService.getAlias() === null) {
                    if (angular.isDefined(toParams.database)) {
                        KayleeService.initializeDatabase(toParams.database);
                    } else {
                        log.error('no database parameter defined');
                    }
                }

                if (angular.isDefined(toParams.roomId)) {
                    roomId = toParams.roomId;
                } else if (roomId === null) {
                    log.error('no roomId parameter defined');
                }

                if (angular.isDefined(toParams.preview) && toParams.preview !== null) {
                    if (toParams.preview.toLowerCase() === 'true') {
                        uiPreview = true;
                    } else {
                        uiPreview = false;
                    }
                }

                if (angular.isDefined(toParams.language)) {
                    LanguageService.setLanguage(toParams.language);
                }
            }
        );
    } else {
        SharedPrefs.getString('firebaseAlias', 'serenity').then(function (firebaseAlias) {
            KayleeService.initializeDatabase(firebaseAlias);
        });
    }

    shouldResetRemoteControlCode = process.env.appEnv !== 'browser';

    fsm.goToStart();

    return {
        online: function () {
            return fsm.is('roomOnline');
        },
        getState: function () {
            return fsm.current;
        },
        isDocumentLoaded: isDocumentLoaded,
        isDeviceLoaded: isDeviceLoaded,
        isControllerLoaded: isControllerLoaded,
        isRoomLoaded: isRoomLoaded,
        isUiPreview: isUiPreview,
        onBeforeOnlineChanged: onBeforeOnlineChanged,
        offBeforeOnlineChanged: offBeforeOnlineChanged,
        onOnlineChanged: onOnlineChanged,
        offOnlineChanged: offOnlineChanged,
        onStateChanged: onStateChanged,
        offStateChanged: offStateChanged,
        getController: function () {
            return controller;
        },
        getRoom: function () {
            return room;
        },
        enterSetup: function () {
            $state.go('setup.start');
        },
    };

    function isDocumentLoaded() {
        return (
            fsm.current !== 'none' && fsm.current !== 'start' && fsm.current !== 'documentLoading'
        );
    }

    function isDeviceLoaded() {
        return (
            fsm.current !== 'none' &&
            fsm.current !== 'start' &&
            fsm.current !== 'documentLoading' &&
            fsm.current !== 'documentLoaded' &&
            fsm.current !== 'deviceLoading'
        );
    }

    function isControllerLoaded() {
        return (
            fsm.current === 'controllerLoaded' ||
            fsm.current === 'roomLoading' ||
            fsm.current === 'roomLoaded' ||
            fsm.current === 'roomOnline' ||
            fsm.current === 'roomOffline' ||
            fsm.current === 'controllerUnpaired'
        );
    }

    function isRoomLoaded() {
        return (
            fsm.current === 'roomLoaded' ||
            fsm.current === 'roomOnline' ||
            fsm.current === 'roomOffline'
        );
    }

    function isUiPreview() {
        return uiPreview && room && room.$getControllers().length === 0;
    }

    function onGoToStart() {
        log.info('start');
        fsm.loadDocument();
    }

    function onLoadDocument() {
        log.info('loading document');
        notifyListeners(stateListeners, fsm.current);

        if (document.readyState === 'complete') {
            fsm.handleDocumentLoaded();
        } else {
            window.addEventListener('load', function () {
                fsm.handleDocumentLoaded();
            });
        }
    }

    function onHandleDocumentLoaded() {
        log.info('document loaded');
        notifyListeners(stateListeners, fsm.current);
        fsm.loadDevice();
    }

    function onLoadDevice() {
        log.info('loading device');
        notifyListeners(stateListeners, fsm.current);

        if (process.env.appEnv === 'browser') {
            log.info("skipping device loading since we're in a browser environment");
            fsm.handleDeviceLoaded();
            return;
        }

        document.addEventListener('deviceready', function () {
            fsm.handleDeviceLoaded();
        });
    }

    function onHandleDeviceLoaded() {
        if (process.env.appEnv === 'browser') {
            log.info('device loaded (skipped)');
        } else {
            log.info('device loaded, uuid = ' + device.uuid);
        }

        notifyListeners(stateListeners, fsm.current);

        scheduleRemoteControlCodeRefresh();

        KayleeService.isInitialized().then(function () {
            if (process.env.appEnv !== 'browser') {
                waitForConnectedTimeout = $timeout(onWaitForConnectedTimeout, 10000);
            }

            fsm.waitForConnection();
        });
    }

    function onWaitForConnection() {
        log.info('waiting for connection');
        notifyListeners(stateListeners, fsm.current);

        if (connectionStateChangedListener === null) {
            connectionStateChangedListener = KayleeService.onConnectionStateChanged(
                onConnectionStateChanged
            );
        }
    }

    function onConnectionStateChanged(connected) {
        log.info(connected ? 'connected to database' : 'disconnected from database');

        if (connected) {
            if (waitForConnectedTimeout !== null) {
                $timeout.cancel(waitForConnectedTimeout);
                waitForConnectedTimeout = null;
            }

            if (!KayleeService.isWorkingOnline()) {
                KayleeService.setWorkingOnline(true);
            }

            if (kayleeInfo !== null) kayleeInfo.$offChange(onKayleeInfoChanged);
            if (controller !== null) controller.$offChange(onControllerChanged);
            if (room !== null) room.$offChange(onRoomChanged);

            if (process.env.appEnv === 'browser') {
                fsm.waitForAuth();
            } else {
                DeviceService.loaded().then(function () {
                    fsm.waitForAuth();
                });
            }
        } else {
            if (fsm.current === 'waitingForConnection') {
                // Do nothing. Wait for connection or timeout.
                log.info('continuing to wait for database connection');
            } else {
                if (isRoomLoaded()) {
                    // stay in current state, firebase offline mode handles offline operation
                    log.info(
                        'no state change necessary, database handled by firebase offline operation'
                    );
                } else {
                    if (fsm.current !== 'waitingForConnection') {
                        log.info('room not loaded, waiting for database connection');
                        fsm.waitForConnection();
                    }
                }
            }
        }
    }

    function onWaitForConnectedTimeout() {
        waitForConnectedTimeout = null;

        if (fsm.current !== 'waitingForConnection') {
            log.error('state ' + fsm.current + ' !== waitingForConnection');
        }

        log.debug('timed out waiting for database connection');

        idbKeyval.get('kayleeCache').then(function (cache) {
            if (typeof cache === 'undefined' || cache === null) {
                log.debug('no database cache found; waiting for database connection');
            } else {
                log.debug('using database cache');
                KayleeService.setWorkingOnline(false, cache);
                log.debug('cache restored');
                DeviceService.loaded().then(function () {
                    log.debug('DeviceService loaded; loading kaylee');
                    fsm.loadKaylee();
                });
            }
        });
    }

    function onWaitForAuth() {
        log.info('waiting for authentication');
        notifyListeners(stateListeners, fsm.current);

        prevAuthUid = null;

        if (process.env.appEnv === 'browser') {
            // We assume that login is handled by Mal, and simply wait for the
            // auth state change here.
            KayleeService.onAuthStateChanged(onAuthStateChanged);
        } else {
            if (!KayleeService.isAuthenticatedAsController()) {
                signInAsController().catch(function (err) {
                    log.error('failed authenticating as controller, error: ' + err);
                });
            }

            KayleeService.onAuthStateChanged(onAuthStateChanged);

            if (authRetryTimeout === null) {
                authRetryTimeout = $timeout(onAuthRetryTimeout, authRetryDelayMs);
            }
        }
    }

    function signInAsController() {
        return SharedPrefs.getString('serenityAuthSecret', '0')
            .then(function (secret) {
                if (secret === '0') {
                    secret = getRandomString(128);
                    return SharedPrefs.setString('serenityAuthSecret', secret).then(function () {
                        return secret;
                    });
                }

                return secret;
            })
            .then(function (secret) {
                return $q.all([$q.resolve(secret), cordova.getAppVersion.getVersionCode()]);
            })
            .then(function (data) {
                var secret = data[0];
                var appVersionCode = data[1];

                return KayleeService.signInAsController({
                    secret: secret,
                    model: device.model,
                    serialNumber: DeviceService.getSerialNumber(),
                    platform: device.platform,
                    platformVersion: device.version,
                    uuid: device.uuid,
                    appName: 'inara',
                    appVersionCode: appVersionCode,
                    kayleeVersion: 2,
                });
            });
    }

    function onAuthStateChanged(authFbUser) {
        fbUser = authFbUser;

        if (fbUser === null) {
            prevAuthUid = null;
            fsm.handleUnauth();
        } else {
            if (prevAuthUid === fbUser.uid) {
                log.debug(
                    'onAuthStateChanged: uid (' +
                        fbUser.uid +
                        ") didn't change, so not triggering state change"
                );
                return;
            }

            prevAuthUid = fbUser.uid;
            fsm.handleAuth();
        }
    }

    function onHandleUnauth() {
        log.info('unauthenticated');

        controller = null;
        resetRoomData();
        notifyListeners(stateListeners, fsm.current);

        if (process.env.appEnv === 'browser') {
            if ($state.current.name !== '' && !$state.is('start') && !$state.includes('setup')) {
                log.debug('onHandleUnauth: forcing transition to start');
                $state.go('start', { roomId: roomId });
            }
        } else {
            if (!$state.is('start') && !$state.includes('setup') && !$state.is('update')) {
                log.debug('onHandleUnauth: forcing transition to start');
                $state.go('start');
            }

            if (authRetryTimeout === null) {
                authRetryTimeout = $timeout(onAuthRetryTimeout, authRetryDelayMs);
            }
        }
    }

    function onHandleAuth() {
        if (KayleeService.isAuthenticatedAsAnonymousRemoteControl(fbUser)) {
            log.info('authenticated as anonymous remote control');
        } else if (KayleeService.isAuthenticatedAsUser(fbUser)) {
            log.info('authenticated as user <' + fbUser.email + '>');
        } else if (KayleeService.isAuthenticatedAsController(fbUser)) {
            log.info('authenticated as controller');
        } else {
            log.error(
                'unrecognized authentication: ' +
                    JSON.stringify({
                        displayName: fbUser.displayName,
                        email: fbUser.email,
                        isAnonymous: fbUser.isAnonymous,
                        providerData: fbUser.providerData,
                        providerId: fbUser.providerId,
                        uid: fbUser.uid,
                    })
            );
        }

        if (process.env.appEnv !== 'browser') {
            if (authRetryTimeout !== null) {
                $timeout.cancel(authRetryTimeout);
                authRetryTimeout = null;
            }
        }

        controller = null;
        resetRoomData();
        notifyListeners(stateListeners, fsm.current);
        fsm.loadKaylee();
    }

    function onAuthRetryTimeout() {
        authRetryTimeout = null;

        if (KayleeService.isAuthenticated()) {
            log.debug("auth retry timeout expired, but we're authenticated, so nothing to do");
            return;
        }

        log.info('retrying authentication');
        signInAsController();

        authRetryTimeout = $timeout(onAuthRetryTimeout, authRetryDelayMs);
    }

    function onLoadKaylee() {
        log.info('loading kaylee');
        notifyListeners(stateListeners, fsm.current);

        kayleeInfo = KayleeService.getKayleeInfo();
        kayleeInfo.$onChange(onKayleeInfoChanged);
    }

    function onKayleeInfoChanged() {
        log.info('kaylee info changed');

        if (KayleeService.isWorkingOnline() && !KayleeService.isAuthenticated()) {
            log.error('kayleeInfo changed while unauthenticated');
            return;
        }

        if (!kayleeInfo.hasOwnProperty('enabled') || !kayleeInfo.enabled) {
            fsm.waitForKayleeEnabled();
        } else if (
            !kayleeInfo.hasOwnProperty('version') ||
            kayleeInfo.version !== knownKayleeVersion
        ) {
            fsm.waitForKayleeVersion();
        } else if (
            fsm.current === 'waitingForKayleeEnabled' ||
            fsm.current === 'waitingForKayleeVersion' ||
            fsm.current === 'kayleeLoading'
        ) {
            fsm.handleKayleeLoaded();
        }
    }

    function onWaitForKayleeEnabled() {
        log.info('waiting for kaylee enabled');
        log.debug('kayleeInfo.enabled = ' + kayleeInfo.enabled);
        notifyListeners(stateListeners, fsm.current);
        resetRoomData();

        if (!$state.is('start') && !$state.includes('setup')) {
            log.debug('onWaitForKayleeEnabled: forcing transition to start');

            if (process.env.appEnv === 'browser') {
                $state.go('start', { roomId: roomId });
            } else {
                $state.go('start');
            }
        }
    }

    function onWaitForKayleeVersion() {
        log.info('waiting for kaylee version');
        log.debug('kayleeInfo.version = ' + kayleeInfo.version);
        notifyListeners(stateListeners, fsm.current);
        resetRoomData();

        if (!$state.is('start') && !$state.includes('setup')) {
            log.debug('onWaitForKayleeVersion: forcing transition to start');

            if (process.env.appEnv === 'browser') {
                $state.go('start', { roomId: roomId });
            } else {
                $state.go('start');
            }
        }
    }

    function onHandleKayleeLoaded() {
        log.info('kaylee loaded');
        notifyListeners(stateListeners, fsm.current);
        fsm.loadController();
    }

    function onLoadController() {
        log.info('loading controller');
        notifyListeners(stateListeners, fsm.current);

        if (process.env.appEnv === 'browser') {
            log.info("skipping controller loading since we're in a browser environment");
            fsm.handleControllerLoaded();
            return;
        }

        var serialNumber = DeviceService.getSerialNumber();
        KayleeService.getControllerBySerialNumber(serialNumber)
            .then(function (resolvedController) {
                if (fsm.current !== 'controllerLoading') return $q.reject('abort');
                controller = resolvedController;
                return controller.$loaded();
            })
            .then(function () {
                if (fsm.current !== 'controllerLoading') return $q.reject('abort');
                var inaraInfo = KayleeService.getInaraInfo();
                return inaraInfo.$loaded();
            })
            .then(function () {
                if (fsm.current !== 'controllerLoading') return $q.reject('abort');
                fsm.handleControllerLoaded();
            })
            .catch(function (err) {
                if (err === 'abort') {
                    log.debug('onLoadController: not in controllerLoading state, aborting');
                    return;
                }

                log.error('failed loading controller, error: ' + err);
            });
    }

    function onHandleControllerLoaded() {
        log.info('controller loaded');
        notifyListeners(stateListeners, fsm.current);

        if (process.env.appEnv === 'browser') {
            fsm.loadRoom();
            return;
        }

        controller.$onChange(onControllerChanged);
    }

    function onControllerChanged() {
        if (
            factoryInstallMode === null ||
            factoryInstallMode !== controller.settings.factoryInstallMode
        ) {
            if (factoryInstallMode === null) {
                log.info('factory install mode: ' + controller.settings.factoryInstallMode);
            } else {
                log.info(
                    'factory install mode changed to ' + controller.settings.factoryInstallMode
                );
            }

            factoryInstallMode = controller.settings.factoryInstallMode;

            if (factoryInstallMode && !$state.is('factoryInstall') && !$state.is('update')) {
                log.debug('onControllerChanged: forcing transition to factoryInstall');
                $state.go('factoryInstall');
            } else if (!factoryInstallMode && $state.is('factoryInstall')) {
                log.debug('onControllerChanged: forcing transition to start');
                $state.go('start');
            }
        }

        var newRoomId = controller.hasOwnProperty('roomId') ? controller.roomId : null;

        log.debug('roomId = ' + roomId + ', newRoomId = ' + newRoomId);

        if (roomId === newRoomId) {
            // If nothing relevant has changed, then there's nothing to do,
            // so return. This is assuming the room is online. The exception is
            // when we are transitioning from being disconnected from the
            // database to being connected to the database. In this case,
            // nothing in the controller changed, but we want to load the room
            // and keep the state machine going.

            if (roomId !== null && fsm.current === 'controllerLoaded') {
                fsm.loadRoom();
            } else if (roomId === null && fsm.current !== 'controllerUnpaired') {
                fsm.handleControllerUnpaired();
            }

            return;
        }

        roomId = newRoomId;

        if (roomId === null) {
            log.info('room id changed to null');
        } else {
            log.info('room id changed to (' + controller.roomId + ')');
        }

        resetRoomData();

        if (
            !$state.is('start') &&
            !$state.includes('setup') &&
            !$state.is('update') &&
            !factoryInstallMode
        ) {
            log.debug('onControllerChanged: forcing transition to start');
            $state.go('start');
        }

        if (roomId === null) {
            fsm.handleControllerUnpaired();
        } else {
            fsm.loadRoom();
        }
    }

    function onHandleControllerUnpaired() {
        log.info('controller unpaired');
        notifyListeners(stateListeners, fsm.current);
    }

    function onLoadRoom() {
        log.info('loading room');
        notifyListeners(stateListeners, fsm.current);
        room = KayleeService.getRoom(roomId);

        return room
            .$loaded()
            .then(function () {
                if (fsm.current !== 'roomLoading') return $q.reject('abort');
                var controllers =
                    process.env.appEnv === 'browser'
                        ? room.$getControllers().$loaded()
                        : $q.resolve();
                var equipmentTypes = KayleeService.getEquipmentTypes();
                var equipment = room.$getEquipment();
                var roomControlCommands = room.$getCommands();
                var site = KayleeService.getSite(room.siteId);
                var company = KayleeService.getCompany(room.companyId);
                var calls = room.$getCalls(defaults.inara.numRecentCalls);
                var directory = room.$getDirectory();

                return $q
                    .all([
                        controllers,
                        equipmentTypes.$loaded(),
                        equipment.$loaded(),
                        roomControlCommands.$loaded(),
                        site.$loaded(),
                        company.$loaded(),
                        calls.$loaded(),
                        directory.$loaded(),
                    ])
                    .then(function () {
                        if (fsm.current !== 'roomLoading') return $q.reject('abort');
                        fsm.handleRoomLoaded();
                    });
            })
            .catch(function (error) {
                if (error === 'abort') {
                    log.debug('onLoadRoom: not in roomLoading state, aborting');
                    return;
                }

                log.debug(error);
                if (process.env.appEnv === 'browser' && error.code === 'PERMISSION_DENIED') {
                    // If the room fails to load due to permissions and we are
                    // in the browser, prompt the user to log in again.
                    fsm.handleUnauth();
                }
            });
    }

    function onHandleRoomLoaded() {
        log.info('room loaded: ' + room);
        notifyListeners(stateListeners, fsm.current);

        prevOnlineState = room.states.onlineState;
        room.$onChange(onRoomChanged);
        remoteControlSessions = room.$getRemoteControlSessions();
        remoteControlSessions.$onChildAdded(onRemoteControlSessionAdded);
        remoteControlSessions.$onChildChanged(onRemoteControlSessionChanged);
        remoteControlSessions.$onChildRemoved(onRemoteControlSessionRemoved);

        if (room.states.onlineState === 'offline') {
            fsm.handleRoomOffline();
        } else {
            fsm.handleRoomOnline();
        }
    }

    function onRoomChanged() {
        if (room.states.onlineState === prevOnlineState) return;
        log.info(
            'room online state changed from ' + prevOnlineState + ' to ' + room.states.onlineState
        );

        if (prevOnlineState === 'offline' && room.states.onlineState !== 'offline') {
            prevOnlineState = room.states.onlineState;
            fsm.handleRoomOnline();
        } else if (
            (prevOnlineState === 'limited' || prevOnlineState === 'online') &&
            room.states.onlineState === 'offline'
        ) {
            prevOnlineState = room.states.onlineState;
            fsm.handleRoomOffline();
        } else {
            prevOnlineState = room.states.onlineState;
        }
    }

    function onBeforeHandleRoomOffline() {
        log.info('room is offline');
        notifyListeners(beforeOnlineListeners, false);
    }

    function onHandleRoomOffline() {
        notifyListeners(stateListeners, fsm.current);

        if (
            !$state.is('start') &&
            !$state.includes('setup') &&
            !$state.is('update') &&
            !factoryInstallMode
        ) {
            log.debug('onHandleRoomOffline: forcing transition to start');
            if (process.env.appEnv === 'browser') {
                $state.go('start', { roomId: roomId });
            } else {
                $state.go('start');
            }
        }

        notifyListeners(onlineListeners, false);
    }

    function onBeforeHandleRoomOnline() {
        log.info('room is online');
        notifyListeners(beforeOnlineListeners, true);
    }

    function onHandleRoomOnline() {
        // Get a remote control code if we don't have one or if ours is stale.

        if (
            shouldResetRemoteControlCode &&
            (sutl.getNode(room, ['remoteControl', 'code']) === null ||
            Date.now() - sutl.getNode(room, ['remoteControl', 'time'], 0) > 60 * 60 * 1000)
        ) {
            log.info('resetting remote control code during start-up');

            room.$resetRemoteControlCode(false).catch(function (err) {
                log.info('failed resetting remote control code, error: ' + JSON.stringify(err));
                log.debug('room online = ' + fsm.is('roomOnline'));
                log.info('will retry in 4 seconds');

                $timeout(function () {
                    if (fsm.is('roomOnline') && room !== null) {
                        room.$resetRemoteControlCode(false)
                            .then(function () {
                                log.info('retry of remote control reset successful');
                            })
                            .catch(function (err) {
                                log.error(
                                    'retry of remote control reset failed, error: ' +
                                        JSON.stringify(err)
                                );
                                log.debug('room online = ' + fsm.is('roomOnline'));
                            });
                    }
                }, 4000);
            });
        }

        notifyListeners(stateListeners, fsm.current);
        notifyListeners(onlineListeners, true);
    }

    function resetRoomData() {
        if (room !== null) room.$offChange(onRoomChanged);

        room = null;

        if (remoteControlSessions !== null) {
            remoteControlSessions.$offChildAdded(onRemoteControlSessionAdded);
            remoteControlSessions.$offChildChanged(onRemoteControlSessionChanged);
            remoteControlSessions.$offChildRemoved(onRemoteControlSessionRemoved);
            remoteControlSessions = null;
        }

        if (prevOnlineState !== 'offline') {
            prevOnlineState = 'offline';
            log.info('room is offline');
            notifyListeners(onlineListeners, false);
        }
    }

    function onRemoteControlSessionAdded(sessionId) {
        scheduleRemoteControlSessionExpirationTimeout();
    }

    function onRemoteControlSessionChanged(sessionId) {
        scheduleRemoteControlSessionExpirationTimeout();
        checkTemporaryAuthentication();
    }

    function onRemoteControlSessionRemoved(sessionId) {
        checkTemporaryAuthentication();
    }

    function scheduleRemoteControlSessionExpirationTimeout() {
        if (remoteControlSessionExpirationTimeout !== null) {
            $timeout.cancel(remoteControlSessionExpirationTimeout);
        }

        var soonestExpirationTime = null;

        for (var i = 0; i < remoteControlSessions.length; i++) {
            var session = remoteControlSessions[i];

            if (
                session.expires > Date.now() &&
                (soonestExpirationTime === null || session.expires < soonestExpirationTime)
            ) {
                soonestExpirationTime = session.expires;
            }
        }

        if (soonestExpirationTime !== null) {
            var delay = soonestExpirationTime - Date.now() + 10;
            remoteControlSessionExpirationTimeout = $timeout(checkTemporaryAuthentication, delay);
        }
    }

    function checkTemporaryAuthentication() {
        // If we are a temporary session and have expired, then go to the
        // unauthenticated state

        if (
            KayleeService.isAuthenticatedAsAnonymousRemoteControl() ||
            (KayleeService.isAuthenticatedAsUser() &&
                !KayleeService.isRole(KayleeService.getUser(), room, 'helper'))
        ) {
            if (remoteControlSessions === null && !fsm.is('unauthenticated')) {
                fsm.handleUnauth();
                return;
            }

            remoteControlSessions.$loaded().then(function (sessions) {
                sessions.$getThisSessionId().then(function (thisSessionId) {
                    if (
                        thisSessionId === null ||
                        sutl.getNode(sessions.$recordAt(thisSessionId), 'expires', 0) <= Date.now()
                    ) {
                        if (!fsm.is('unauthenticated')) fsm.handleUnauth();
                    }
                });
            });
        }
    }

    function onBeforeOnlineChanged(listener) {
        addListener(beforeOnlineListeners, listener, fsm.is('roomOnline'));
    }

    function onOnlineChanged(listener) {
        addListener(onlineListeners, listener, fsm.is('roomOnline'));
    }

    function offBeforeOnlineChanged(listener) {
        removeListener(beforeOnlineListeners, listener);
    }

    function offOnlineChanged(listener) {
        removeListener(onlineListeners, listener);
    }

    function onStateChanged(listener) {
        addListener(stateListeners, listener, fsm.current);
    }

    function offStateChanged(listener) {
        removeListener(stateListeners, listener);
    }

    function addListener(listeners, listener, currentValue) {
        listeners.push(listener);
        listener(currentValue);
    }

    function removeListener(listeners, listener) {
        for (var i = 0; i < listeners.length; i++) {
            if (listeners[i] === listener) {
                listeners.splice(i, 1);
                break;
            }
        }
    }

    function notifyListeners(listeners, value) {
        // Build a temporary list of listeners to loop over in case
        // someone adds or removes a listener in their handler.

        var tempListeners = [];
        var i;

        for (i = 0; i < listeners.length; i++) {
            tempListeners.push(listeners[i]);
        }

        for (i = 0; i < tempListeners.length; i++) {
            tempListeners[i](value);
        }
    }

    function getRandomString(length) {
        // Modeled after base64 web-safe chars, but ordered by ASCII. This is
        // also the same character set used to generate Firebase keys.
        var chars = '-0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ_abcdefghijklmnopqrstuvwxyz';
        var array = new Uint8Array(length);
        window.crypto.getRandomValues(array);
        var randomChars = [];

        array.forEach(function (value) {
            var idx = Math.floor((value * (chars.length - 1)) / 255);
            randomChars.push(chars.charAt(idx));
        });

        return randomChars.join('');
    }

    function scheduleRemoteControlCodeRefresh() {
        if (!shouldResetRemoteControlCode) return;
        var currentMinute = moment().minute();
        const minute = 45;
        let scheduleIntervalMinutes =
            currentMinute < minute ? minute - currentMinute : 60 - currentMinute + minute;
        if (scheduleIntervalMinutes > 70) scheduleIntervalMinutes -= 60;
        if (scheduleIntervalMinutes < 10) scheduleIntervalMinutes += 60;

        // Add 3 minutes of randomness to spread the database load.

        var scheduleIntervalMs =
            scheduleIntervalMinutes * 60 * 1000 + (Math.random() * 3) * 60 * 1000;

        log.debug(
            'scheduling remote control code reset at ' +
                moment(Date.now() + scheduleIntervalMs).format()
        );

        refreshRemoteControlCodeTimeout = $timeout(refreshRemoteControlCode, scheduleIntervalMs);
    }

    function refreshRemoteControlCode() {
        refreshRemoteControlCodeTimeout = null;

        if (isRoomLoaded() && room !== null && KayleeService.isWorkingOnline()) {
            KayleeService.getSite(room.siteId)
                .$loaded()
                .then(function (site) {
                    if (sutl.getNode(site, 'settings.remoteControlEnabled', false)) {
                        log.debug('resetting remote control code');
                        room.$resetRemoteControlCode();
                    }
                });
        }

        if (refreshRemoteControlCodeTimeout === null) scheduleRemoteControlCodeRefresh();
    }
}

module.exports = RoomService;
